import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    Input,
} from '@angular/core';
import { NgbActiveModal, NgbModal, NgbModalRef } from '@cmps/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';

import { AuthService } from '../../../auth/auth.service';
import { CoachingType, GoalStatus } from '../../../coaching-base/general';
import { OpportunityType } from '../../../coaching-base/opportunity';
import { PeriodType } from '../../../widget/date-range.widget.model';
import { TranslationKeyMap } from '../../coaching-insight.i18n';
import {
    getUniqueFromSet,
    PeriodTypeId,
} from '../models/coaching.global.model';
import {
    Commitment,
    CommitmentDetails,
    CommitmentListEntry,
    Goal,
    goalVariancePercent,
    OaPerson,
    Opportunity,
    utcDateStringNoTime,
} from '../models/oa.model';
import { CoachingInsightWidgetService } from '../services/coaching-insight.widget.service';
import { CoachingService } from '../services/coaching.service';
import { TeamOAService } from '../services/team-oa.service';

interface CommitmentGroup {
    title: string;
    commitments: Commitment[];
    activeCommitment: number;
}

@Component({
    selector: 'cmps-current-commitments',
    templateUrl: './team-oa-current-commitments.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TeamOACurrentCommitmentsComponent {
    @Input() member: OaPerson;
    @Input() opportunityselected: Opportunity;
    @Input() openNonKPI: boolean;
    @Input() periodType: PeriodType;

    i18n = TranslationKeyMap;
    PeriodTypeId = PeriodTypeId;

    PeriodType = PeriodType;

    oacommitments: Commitment[] = [];
    commitments: Commitment[] = [];
    commitmentExpanded: boolean = false;

    config: any = {};

    private readonly _modal: NgbModalRef;

    hasPendingData: boolean = false;

    commitmentgroup: CommitmentGroup[] = [];

    commitmentDetailSub: Subscription;

    /** @private */ _GoalStatus = GoalStatus;

    gettingData = false;

    constructor(
        private readonly _coachInsightService: CoachingInsightWidgetService,
        private readonly _authService: AuthService,
        private readonly _teamOAService: TeamOAService,
        private readonly _coachingService: CoachingService,
        private readonly _modalService: NgbModal,
        public readonly activeModal: NgbActiveModal,
        private readonly cd: ChangeDetectorRef,
        private readonly _translateService: TranslateService,
    ) {}

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    get user() {
        return this._authService.user;
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    get periodTypeId() {
        return this._coachInsightService.periodTypeId;
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    expandCommitment(groupindex: number, commitmentindex: number) {
        const group = this.commitmentgroup[groupindex];
        const comm = group.commitments[commitmentindex];

        // close
        if (group.activeCommitment === commitmentindex) {
            this.commitmentExpanded = false;
            group.activeCommitment = null;

            comm.details = undefined;

            if (this.commitmentDetailSub) {
                this.commitmentDetailSub.unsubscribe();
            }

            this.hasPendingData = false;
            // eslint-disable-next-line @typescript-eslint/dot-notation
            if (!this.cd['destroyed']) {
                this.cd.detectChanges();
            }
            // open
        } else {
            this.commitmentExpanded = true;
            group.activeCommitment = commitmentindex;

            if (!comm.details) {
                this.retrieveCommitmentData(comm);
            }
        }
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    retrieveCommitmentData(comm: Commitment, triggerHasPendingData = true) {
        this.hasPendingData = true;

        const x = this._coachingService.retrieveCommitmentDetails(
            comm.commitmentId,
            this.member.userId,
            this._coachInsightService.getPeriodTypeId(),
            this.user.languageId,
        );
        this.commitmentDetailSub = x.subscribe(
            (data) => {
                if (data) {
                    let details: CommitmentDetails = {};
                    const commitment = data.commitment;
                    const goals = data.goals;
                    const observations = data.observations;

                    // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                    if (commitment && commitment.coachingEventId) {
                        commitment.commitmentId = commitment.coachingEventId;
                    }

                    details = {
                        commitment,
                        goals,
                        observations,
                    };

                    // eslint-disable-next-line @typescript-eslint/no-unused-vars
                    const metrics: Opportunity[] = this.member.opportunities;
                    const oa: Opportunity[] = this.member.coached.concat(
                        this.member.uncoached,
                    );
                    const nonoa: Opportunity[] = this.member.nonoa;

                    comm.activeOAGoalCount = this.countMetrics(goals, oa);

                    comm.activeNonOAGoalCount = this.countMetrics(goals, nonoa);

                    comm.observationCount = observations.length;

                    comm.details = details;

                    // CALCULATE PERCENT TO GOAL!!!
                    comm.details.goals = details.goals.map((g) => {
                        if (g.actualMTDInitValue != null) {
                            g.percentGoalInit = goalVariancePercent(
                                g.actualMTDInitValue,
                                g.goalValue,
                                g.rankOrder,
                            );
                        }
                        if (g.actualMTDValue != null) {
                            g.percentGoal = goalVariancePercent(
                                g.actualMTDValue,
                                g.goalValue,
                                g.rankOrder,
                            );
                        }

                        // extract the trend
                        const a = g.monthTrendInit
                            ? g.monthTrendInit.split('.')[0]
                            : null;
                        if (a) {
                            g.actualMonthTrendInit = a.toLowerCase();
                        }

                        const b = g.monthTrend
                            ? g.monthTrend.split('.')[0]
                            : null;
                        if (b) {
                            g.actualMonthTrend = b.toLowerCase();
                        }

                        if (
                            g.currentPerformanceGoalStatusId ===
                                GoalStatus.ACTIVE &&
                            g.expectedCompletionDate &&
                            this.compareGoalEndDate(
                                g.expectedCompletionDate,
                                new Date(),
                            )
                        ) {
                            g.currentPerformanceGoalStatus =
                                this._translateService.instant(
                                    this.i18n._7522_EXPIRED,
                                );
                        }

                        return g;
                    });
                }

                setTimeout(() => {
                    this.hasPendingData = false;
                    this.gettingData = false;
                    // eslint-disable-next-line @typescript-eslint/dot-notation
                    if (!this.cd['destroyed']) {
                        this.cd.detectChanges();
                    }
                }, 800);

                // eslint-disable-next-line n/handle-callback-err
            },
            // eslint-disable-next-line n/handle-callback-err
            (error) => {
                this.hasPendingData = false;
                // eslint-disable-next-line @typescript-eslint/dot-notation
                if (!this.cd['destroyed']) {
                    this.cd.detectChanges();
                }
            },
        );
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    compareGoalEndDate(endDate: string | Date, today: string | Date) {
        console.log(
            utcDateStringNoTime(endDate),
            '<',
            utcDateStringNoTime(today),
        );
        return utcDateStringNoTime(endDate) < utcDateStringNoTime(today);
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    countMetrics(goals: Goal[], metrics: Opportunity[]) {
        // eslint-disable-next-line array-callback-return
        return goals.filter((g: Goal) => {
            // look for matched primary metric, if not found, look for one of its components
            const foundmetric = metrics.find((m: Opportunity) => {
                let a = m.metricId === g.metricId;
                if (!a && m.components && m.components.length) {
                    a =
                        m.components.find((h) => h.metricId === g.metricId) !=
                        null;
                }

                return a;
            });

            if (!foundmetric) return false;
            else if (
                // this goal is from the ForWidget SP
                (g.currentPerformanceGoalStatusId === GoalStatus.ACTIVE ||
                    g.currentPerformanceGoalStatusId ===
                        GoalStatus.PENDING_CLOSE ||
                    g.currentPerformanceGoalStatusId === GoalStatus.CLOSED) &&
                foundmetric != null
            ) {
                return true;
            }
        }).length;
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    ngOnChanges(args) {
        // sort coommitments
        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain, no-prototype-builtins
        if (args && args.hasOwnProperty('opportunityselected')) {
            setTimeout(() => {
                this.sortCommitmentsByopportunityselected();
            }, 1);
        }
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    ngOnDestroy() {
        if (this.commitmentDetailSub) {
            this.commitmentDetailSub.unsubscribe();
        }
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    sortCommitmentsByopportunityselected() {
        let x: Commitment[] = [];
        let y: Commitment[] = [];
        let only1nonkpi = false;

        const commitments = this.member.allcommitments;

        x.push(
            // ...((this.opportunityselected && this.opportunityselected.commitments) ? this.opportunityselected.commitments : [])
            ...// eslint-disable-next-line @typescript-eslint/prefer-optional-chain
            (commitments && commitments.length
                ? commitments.filter((f: CommitmentListEntry) => {
                      const coachedcomps =
                          this.opportunityselected.coachedComponents;
                      let foundcomponent = [];
                      // eslint-disable-next-line @typescript-eslint/prefer-includes
                      const foundparent =
                          // eslint-disable-next-line @typescript-eslint/prefer-includes
                          f.metricIds.indexOf(
                              this.opportunityselected.metricId,
                          ) > -1;
                      // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                      if (coachedcomps && coachedcomps.length) {
                          // eslint-disable-next-line @typescript-eslint/prefer-includes
                          foundcomponent = coachedcomps.filter(
                              // eslint-disable-next-line @typescript-eslint/prefer-includes
                              (a) => f.metricIds.indexOf(a.metricId) > -1,
                          );
                      }
                      // if parent doesn't have opp/goal, check components
                      if (
                          this.opportunityselected.coachingType ===
                              CoachingType.NON_OA ||
                          this.opportunityselected.opportunityType ===
                              OpportunityType.COMPONENT
                      ) {
                          return (
                              (foundparent || foundcomponent.length) &&
                              !f.isNonKpi
                          );
                      } else {
                          return foundparent && !f.isNonKpi;
                      }
                  })
                : []),
        );

        const matchedcommids = x.map((g) => g.commitmentId);

        y.push(
            ...this.member.allcommitments.filter(
                // eslint-disable-next-line @typescript-eslint/prefer-includes
                (f: CommitmentListEntry) =>
                    // eslint-disable-next-line @typescript-eslint/prefer-includes
                    matchedcommids.indexOf(f.commitmentId) === -1,
            ),
        );

        x = getUniqueFromSet(x, 'commitmentId');
        y = getUniqueFromSet(y, 'commitmentId');

        let rel = null;
        let unrel = null;
        const only1x = x.length === 1;
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const only1y = y.length === 1;
        let alreadyexpanded = false;

        if (x.length) {
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
            rel = {
                title: this._translateService.instant(
                    TranslationKeyMap._7389_CURRENT_COMMITMENTS,
                ),
                commitments: x,
                activeCommitment: null,
            } as CommitmentGroup;

            // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
            if (this.opportunityselected && this.opportunityselected.metric) {
                // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
                rel.subtitle = `${this._translateService.instant(
                    TranslationKeyMap._7305_FOR,
                )} ${this.opportunityselected.metric.metricName}`;
            }

            x.forEach((f) => {
                this.gettingData = true;
                this.retrieveCommitmentData(f, false);
            });
        }

        if (y.length) {
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
            unrel = {
                title: rel
                    ? this._translateService.instant(
                          TranslationKeyMap._7466_OTHER_COMMITMENTS,
                      )
                    : this._translateService.instant(
                          TranslationKeyMap._7389_CURRENT_COMMITMENTS,
                      ),
                commitments: y,
                activeCommitment: null,
            } as CommitmentGroup;

            y.forEach((f) => {
                this.gettingData = true;
                this.retrieveCommitmentData(f, false);
            });
        }

        setTimeout(() => {
            this.commitmentgroup = [];
            this.commitmentExpanded = false;

            if (rel) {
                this.commitmentgroup.push(rel);
                if (this.opportunityselected && only1x) {
                    this.expandCommitment(0, 0);
                    alreadyexpanded = true;
                }
            }
            if (unrel) {
                if (this.openNonKPI) {
                    // eslint-disable-next-line array-callback-return
                    unrel.commitments.sort(
                        // eslint-disable-next-line array-callback-return
                        (a: CommitmentListEntry, b: CommitmentListEntry) => {
                            if (!a.isNonKpi && b.isNonKpi) {
                                return 1;
                            } else if (!b.isNonKpi && a.isNonKpi) {
                                return -1;
                            } else if (a.isNonKpi && b.isNonKpi) {
                                return 0;
                            }
                        },
                    );

                    only1nonkpi =
                        unrel.commitments.filter((s) => s.isNonKpi === true)
                            .length === 1;
                }

                this.commitmentgroup.push(unrel);

                // REMOVE openNonKPI if clicking Name and NonKPI button will open the first commitment
                if (this.openNonKPI && only1nonkpi && !alreadyexpanded) {
                    this.expandCommitment(
                        this.commitmentgroup.length > 1 ? 1 : 0,
                        0,
                    );
                }
            }

            // eslint-disable-next-line @typescript-eslint/dot-notation
            if (!this.cd['destroyed']) {
                this.cd.detectChanges();
            }
        }, 10);
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    iframe(eventtype: string, stateOrId: string | number) {
        this._coachingService.openIFrame(
            eventtype,
            stateOrId,
            this.member.userId,
        );
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    track_identify_commitmentgroup(index, item: CommitmentGroup) {
        return item.title;
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    track_identify_commitment(index, item: CommitmentListEntry) {
        return item.commitmentId;
    }
}
